/** Display helpers. Storage is UTC; everything a person reads is Asia/Karachi (CLAUDE.md). */
const TZ = "Asia/Karachi";

const dateTimeFmt = new Intl.DateTimeFormat("en-GB", {
  timeZone: TZ,
  day: "numeric",
  month: "short",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  hourCycle: "h23",
});
const dateFmt = new Intl.DateTimeFormat("en-GB", {
  timeZone: TZ,
  weekday: "short",
  day: "numeric",
  month: "short",
  year: "numeric",
});
/** DateOnly values are already PKT calendar dates: format them in UTC so they never shift. */
const dayFmt = new Intl.DateTimeFormat("en-GB", {
  timeZone: "UTC",
  day: "numeric",
  month: "short",
  year: "numeric",
});

/** "19 Oct 2026, 08:00 PKT" from an ISO UTC timestamp. */
export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  return `${dateTimeFmt.format(new Date(iso))} PKT`;
}

/** "Mon 19 Oct 2026" from an ISO UTC timestamp, in Pakistan. */
export function formatDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  return dateFmt.format(new Date(iso)).replace(",", "");
}

/** "18 Oct 2026" from a YYYY-MM-DD date (FBR published date, payment date). */
export function formatDay(day: string | null | undefined): string {
  if (!day) return "—";
  return dayFmt.format(new Date(`${day}T00:00:00Z`));
}

export function shaPrefix(sha: string, n = 12): string {
  return sha.slice(0, n);
}

export function formatPkr(paisa: number): string {
  const rupees = paisa / 100;
  return `PKR ${rupees.toLocaleString("en-PK", { minimumFractionDigits: rupees % 1 ? 2 : 0, maximumFractionDigits: 2 })}`;
}

export function pct(rate: string): string {
  return `${rate}%`;
}

export function sectionLabel(s: string): string {
  return `s.${s}`;
}

export const SOURCE_LABEL = { ATL_IT: "Income-tax ATL", ATL_ST: "Sales-tax ATL" } as const;

export const PAYEE_LABEL: Record<string, string> = {
  company: "Company",
  individual: "Individual",
  aop: "AOP",
  non_resident: "Non-resident",
  unconfirmed: "Unconfirmed",
};

/** "Monday 19 October 2026" from an ISO UTC timestamp, in Pakistan. */
const longDateFmt = new Intl.DateTimeFormat("en-GB", {
  timeZone: TZ,
  weekday: "long",
  day: "numeric",
  month: "long",
  year: "numeric",
});
export function formatLongDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  return longDateFmt.format(new Date(iso)).replace(",", "");
}

const timeFmt = new Intl.DateTimeFormat("en-GB", {
  timeZone: TZ,
  hour: "2-digit",
  minute: "2-digit",
  hourCycle: "h23",
});
/** "08:00" (PKT) from an ISO UTC timestamp. */
export function formatTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  return timeFmt.format(new Date(iso));
}

const dayMonthFmt = new Intl.DateTimeFormat("en-GB", { timeZone: TZ, day: "numeric", month: "short" });
/** "18 Oct" (PKT) from an ISO UTC timestamp. */
export function formatDayMonth(iso: string | null | undefined): string {
  if (!iso) return "—";
  return dayMonthFmt.format(new Date(iso));
}

/** "18 Oct" from a YYYY-MM-DD date: the short form used in running text. */
export function formatDayShort(day: string | null | undefined): string {
  if (!day) return "—";
  return formatDay(day).replace(/ \d{4}$/, "");
}

const PKT_OFFSET_MS = 5 * 60 * 60 * 1000;
const DAY_MS = 24 * 60 * 60 * 1000;
const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/**
 * The next scheduled Monday check, 08:00 PKT (Pakistan keeps no daylight saving), as "Mon 26 Oct 08:00".
 * This is the published schedule, not a statement about any one tenant's run.
 */
export function nextMondayCheck(now: Date = new Date()): string {
  const pkt = new Date(now.getTime() + PKT_OFFSET_MS); // PKT wall clock, read with the UTC getters
  const today0800 = Date.UTC(pkt.getUTCFullYear(), pkt.getUTCMonth(), pkt.getUTCDate(), 8);
  let next = today0800 + ((8 - pkt.getUTCDay()) % 7) * DAY_MS;
  if (next <= pkt.getTime()) next += 7 * DAY_MS;
  const d = new Date(next);
  return `${WEEKDAYS[d.getUTCDay()]} ${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]} 08:00`;
}

/**
 * The Pakistan calendar date (YYYY-MM-DD) of an ISO UTC timestamp. API date parameters are PKT dates;
 * `iso.slice(0, 10)` is the UTC date, a day early for anything between 00:00 and 05:00 PKT.
 */
export function pktDate(iso: string): string {
  return new Date(Date.parse(iso) + PKT_OFFSET_MS).toISOString().slice(0, 10);
}

/**
 * The first day (YYYY-MM-DD) of the Pakistan tax year that `now` falls in: 1 July of this year from
 * 00:00 PKT on 1 July, else 1 July of last year. Tax year 2027 runs 1 Jul 2026 to 30 Jun 2027.
 */
export function taxYearStart(now: Date = new Date()): string {
  const today = pktDate(now.toISOString());
  const year = Number(today.slice(0, 4));
  return `${today.slice(5) >= "07-01" ? year : year - 1}-07-01`;
}

/**
 * The first day (YYYY-MM-DD) of the Pakistan tax year after the one `now` falls in: the default start
 * of a new version of a table already in force, so a correction starts on a future date instead of
 * replacing the table in force back to 1 July.
 */
export function nextTaxYearStart(now: Date = new Date()): string {
  return `${Number(taxYearStart(now).slice(0, 4)) + 1}-07-01`;
}

/** The last full calendar quarter before `now` in PKT, as PKT dates: the period statement's default. */
export function previousQuarter(now: Date = new Date()): { from: string; to: string } {
  const pkt = new Date(now.getTime() + PKT_OFFSET_MS);
  const startOfThis = Date.UTC(pkt.getUTCFullYear(), Math.floor(pkt.getUTCMonth() / 3) * 3, 1);
  const end = new Date(startOfThis - DAY_MS);
  const start = new Date(Date.UTC(end.getUTCFullYear(), end.getUTCMonth() - 2, 1));
  return { from: start.toISOString().slice(0, 10), to: end.toISOString().slice(0, 10) };
}

/** Whole days left until an ISO timestamp, rounded up; 0 once it has passed. */
export function daysLeft(iso: string, now: Date = new Date()): number {
  return Math.max(0, Math.ceil((new Date(iso).getTime() - now.getTime()) / DAY_MS));
}

export const RATE_REASON: Record<string, string> = {
  atl_unknown: "No status yet",
  payee_unconfirmed: "Confirm payee type",
  section_out_of_scope: "Section not covered",
  not_in_table: "Not in rate table",
  no_approved_table: "No approved rate table",
};
